import React, { useState } from "react";
import { useStore } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import { noop } from "lodash";

import { useGetLayerFeatureTotalsQuery } from "fond/api";
import { getCurrentProjectUploads } from "fond/project";
import AddressTypeAlerts from "fond/project/panels/upload/AddressTypeAlerts";
import Upload from "fond/project/panels/upload/Upload";
import { useAddressTypeCheck } from "fond/project/panels/upload/useAddressTypeCheck";
import { beginLayerUpload, dismissUploadError } from "fond/project/redux";
import { advanceBaseDataTask, dismissBaseDataTask, failBaseDataTask, startBaseDataTask, succeedBaseDataTask } from "fond/redux/baseDataTasks";
import { LayerId, LayerIds, Store } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

import { BASE_DATA_LAYERS, BaseDataLayerKey } from "../layers";
import { useBaseDataModelContext } from "../useBaseDataModel";

import ReplaceConfirmation from "./ReplaceConfirmation";
import TaskProgress, { TaskResult } from "./TaskProgress";
import { getBaseDataUploadId, getUploadButtonLabel, getUploadResultMessage } from "./upload";

type LayerFiles = Partial<Record<LayerId, File[]>>;

interface LayerUploadProps {
  layerKey: BaseDataLayerKey;
  /** Called once an upload succeeds and its result is shown. */
  onDone?(): void;
}

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" fontWeight={500} mb={0.5}>
    {children}
  </Typography>
);

/**
 * The expanded Upload option of a base data layer: a file section per planner layer, one Upload button,
 * a replace confirmation when the layers have data, then the upload's progress and result.
 */
const LayerUpload: React.FC<LayerUploadProps> = ({ layerKey, onDone }) => {
  const dispatch = useAppDispatch();
  const store = useStore<Store>();
  const { projectId, versionId } = useAppSelector((state) => state.project);
  const uploadId = getBaseDataUploadId(layerKey);
  const row = useBaseDataModelContext().rows[layerKey];
  const task = row.task?.kind === "upload" ? row.task : null;
  const { refetch: refetchFeatureTotals } = useGetLayerFeatureTotalsQuery({ versionId }, { skip: !versionId });
  const addressTypeCheck = useAddressTypeCheck();

  const { noun, layers: sections } = BASE_DATA_LAYERS[layerKey];
  const isMultiSection = sections.length > 1;

  const [files, setFiles] = useState<LayerFiles>({});
  /** Files of the latest upload, kept to retry it. */
  const [uploadedFiles, setUploadedFiles] = useState<LayerFiles | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  /** Changes to clear the file sections. */
  const [sectionsKey, setSectionsKey] = useState(0);

  const selectedLayerIds = sections.map(({ id }) => id).filter((layerId) => files[layerId]?.length);
  const selectedFiles = Object.fromEntries(selectedLayerIds.map((layerId) => [layerId, files[layerId]])) as LayerFiles;
  const uploadedLayerIds = uploadedFiles ? (Object.keys(uploadedFiles) as LayerId[]) : [];
  const shownSections = isMultiSection && uploadedFiles ? sections.filter(({ id }) => uploadedLayerIds.includes(id)) : [];

  const startUpload = async (layerFiles: LayerFiles) => {
    const layerIds = Object.keys(layerFiles) as LayerId[];
    setIsConfirming(false);
    setUploadedFiles(layerFiles);

    const onSuccess = async () => {
      dispatch(advanceBaseDataTask({ key: layerKey, stage: "processing" }));
      const totals = await refetchFeatureTotals()
        .unwrap()
        .catch(() => undefined);
      const result = sections
        .filter(({ id }) => layerIds.includes(id))
        .map(({ id, title, units }) => ({
          ...(isMultiSection && { title }),
          message: getUploadResultMessage(units, totals?.[id]?.count),
        }));
      dispatch(succeedBaseDataTask({ key: layerKey, result }));
      onDone?.();
    };

    const uploading = dispatch(
      beginLayerUpload(projectId, versionId, uploadId, layerFiles, false, addressTypeCheck.selectedAddressTypeField, onSuccess)
    );
    // Started once the upload has begun, so its progress isn't read from an earlier upload.
    dispatch(startBaseDataTask({ key: layerKey, source: "upload", keys: [layerKey], stage: "uploadingFiles", uploadId }));
    try {
      await uploading;
    } catch {
      const failureMessage = getCurrentProjectUploads(store.getState().project)?.[uploadId]?.failureMessage;
      dispatch(failBaseDataTask({ key: layerKey, error: failureMessage || undefined }));
    }
  };

  const handleUpload = () => {
    if (row.layers.some(({ id, hasData }) => hasData && selectedLayerIds.includes(id))) {
      setIsConfirming(true);
    } else {
      startUpload(selectedFiles);
    }
  };

  const chooseOtherFiles = () => {
    dispatch(dismissUploadError(uploadId));
    dispatch(dismissBaseDataTask(layerKey));
    setFiles({});
    setUploadedFiles(null);
    addressTypeCheck.reset();
    setSectionsKey((key) => key + 1);
  };

  if (task?.status === "failed") {
    return (
      <TaskProgress
        error={{
          title: `Couldn't upload ${noun}`,
          reason: task.error || "The upload didn't finish. Check your connection and try again.",
          actions: [
            { label: "Choose other files", onClick: chooseOtherFiles },
            ...(uploadedFiles ? [{ label: "Retry", primary: true, onClick: () => startUpload(uploadedFiles) }] : []),
          ],
        }}
      />
    );
  }

  if (task?.status === "succeeded") {
    return (
      <Box display="flex" flexDirection="column" gap={1}>
        {task.result.map(({ title, message }) => (
          <Box key={title ?? message}>
            {title && <SectionTitle>{title}</SectionTitle>}
            <TaskResult message={message} />
          </Box>
        ))}
      </Box>
    );
  }

  if (task?.status === "running") {
    const { phase } = task;
    if (shownSections.length === 0) return <TaskProgress title={`Uploading ${noun}`} phase={phase} />;
    return (
      <Box display="flex" flexDirection="column" gap={1.5}>
        {shownSections.map(({ id, title }) => (
          <Box key={id}>
            <SectionTitle>{title}</SectionTitle>
            <TaskProgress phase={phase} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {isConfirming && <ReplaceConfirmation layerNoun={noun} onCancel={() => setIsConfirming(false)} onReplace={() => startUpload(selectedFiles)} />}
      {/* Hidden while confirming so the chosen files are kept. */}
      <Box display={isConfirming ? "none" : "block"}>
        {sections.map(({ id: layerId, title, geometryType }) => (
          <Box key={`${layerId}-${sectionsKey}`}>
            {isMultiSection && <SectionTitle>{title}</SectionTitle>}
            {layerId === LayerIds.inAddress && <AddressTypeAlerts {...addressTypeCheck} />}
            <Upload
              layer={{ id: layerId, name: title, geometryType }}
              autoUpload={false}
              includeLayerName={false}
              onBeginUpload={noop}
              onSelectFiles={(selected) => setFiles((prev) => ({ ...prev, [layerId]: selected }))}
              onRemove={() => {
                setFiles((prev) => ({ ...prev, [layerId]: undefined }));
                if (layerId === LayerIds.inAddress) addressTypeCheck.reset();
              }}
              layerValidator={(features) => {
                if (layerId === LayerIds.inAddress) addressTypeCheck.checkAddresses(features);
              }}
            />
          </Box>
        ))}
        <Box display="flex" justifyContent="flex-end">
          <Button variant="contained" size="small" disabled={selectedLayerIds.length === 0} onClick={handleUpload}>
            {getUploadButtonLabel(selectedLayerIds.length)}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default LayerUpload;
